import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Recycle,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Lock,
  Tag,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Engagements de livraison Versailles ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos engagements de livraison de cartons à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la configuration urbaine et les exigences des résidents
            versaillais imposent une flexibilité absolue, notre service de
            livraison à domicile réinvente ses standards.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Créneau « Sur-Mesure » (ajustable jusqu'à H-2)",
              body: "Un imprévu professionnel ou un changement d'emploi du temps près de l'avenue de Paris ? Vous pouvez décaler l'heure de passage de notre chauffeur sans aucun frais.",
              Icon: Calendar,
            },
            {
              title: "Spécialiste « Accès difficiles & rues étroites »",
              body: "Les voies historiques de Versailles ou les cours intérieures pavées sont parfois complexes. Nos chauffeurs sont équipés de véhicules compacts agiles et de chariots silencieux spécifiques pour acheminer vos commandes au plus près, sans gêner le voisinage.",
              Icon: Truck,
            },
            {
              title: "Garantie « Zéro erreur, relivré à l'heure »",
              body: "Si lors du déballage de votre commande une référence ne correspond pas ou si vous avez sous-estimé vos besoins, nous nous engageons à vous livrer le matériel manquant dans un délai de 4 heures maximum au cours de la même journée.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Programme « Seconde Chance Locale » -40 % ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Programme exclusif
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Le programme « Seconde Chance Locale » : des cartons traçables à
              -40 %
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous cassons les codes du reconditionné classique avec un
              concept d&apos;économie circulaire innovant, transparent et
              engagé au cœur {city.deptNameWithArticle}.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Traçabilité d'un circuit 100 % propre",
                  body: "Nos cartons de seconde main proviennent exclusivement d'opérations institutionnelles et de salons professionnels éphémères. Ils n'ont jamais stocké de produits alimentaires ni transité par des caves. Nous connaissons l'historique exact de chaque lot.",
                  Icon: Lock,
                },
                {
                  title: "Label « Zéro Poussière »",
                  body: "En plus d'un test rigoureux de résistance à la pression verticale, chaque carton fait l'objet d'un dépoussiérage par soufflage industriel avant d'être re-filmé. Vous bénéficiez d'un contenant d'une propreté clinique, idéal pour vos vêtements ou vos textiles.",
                  Icon: Sparkles,
                },
                {
                  title: "Impact solidaire de votre remise de 40 %",
                  body: "En achetant ces boîtes à prix réduit, vous soutenez notre initiative locale : une partie des bénéfices de cette filière est reversée à des associations de réinsertion partenaires chargées du tri des matériaux dans le département. Un choix doublement vertueux.",
                  Icon: Recycle,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-4 md:p-5"
                >
                  <span className="flex items-center justify-center shrink-0 h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-versailles-cta-reconditionne"
            >
              <Tag className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés -40 %
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                -40 %
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cartons tracés, dépoussiérés et contrôlés un par un. Une seconde
              vie utile, écologique et solidaire pour vos déménagements
              {" "}à {city.name}.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Origine 100 % traçable
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Re-filmés à neuf
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Filière solidaire locale
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Comment commander en quelques clics ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            En trois clics
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment lancer votre commande en ligne en quelques clics ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous simplifier la vie, notre plateforme e-commerce a été
            pensée pour être fluide, rapide et intuitive. Organisez vos
            fournitures en un instant.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              step: "1",
              title: "Composez votre panier",
              body: "Parcourez notre boutique et sélectionnez vos cartons standards, renforcés, penderies à vêtements ou nos kits complets pré-calibrés selon la taille de votre logement.",
              Icon: Boxes,
            },
            {
              step: "2",
              title: "Ajustez vos accessoires",
              body: "Ajoutez les outils indispensables pour travailler comme un professionnel : rubans adhésifs techniques, dévidoirs pistolets, rouleaux de film à bulles haute densité.",
              Icon: Layers,
            },
            {
              step: "3",
              title: "Planifiez la livraison",
              body: "Renseignez votre adresse exacte à Versailles et choisissez la date ainsi que l'horaire qui s'insèrent le mieux dans votre emploi du temps. En moins de deux minutes, votre logistique est validée et réservée.",
              Icon: Calendar,
            },
          ].map(({ step, title, body, Icon }) => (
            <div
              key={step}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white font-display font-extrabold text-[15px]">
                {step}
              </div>
              <div className="mt-3 flex items-center gap-2.5">
                <Icon className="h-5 w-5 text-[#237cb5]" strokeWidth={2.4} />
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-versailles-cta-shop"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Composer mon panier maintenant
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Erreurs à éviter lors de l'emballage ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Conseils pros
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les erreurs à éviter lors de l&apos;emballage
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour garantir que vos boîtes supportent le voyage sans
            s&apos;affaisser, appliquez ces règles fondamentales et évitez
            les pièges classiques.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {[
            {
              n: "Erreur n°1",
              title: "Laisser des espaces vides au sommet",
              body: "Un carton qui contient du vide s'écrase inévitablement sous le poids des autres colis empilés par-dessus dans le camion. Utilisez du papier kraft froissé ou votre linge de maison pour combler chaque recoin et rigidifier la boîte.",
            },
            {
              n: "Erreur n°2",
              title: "Surcharger les grands volumes",
              body: "Ne mettez jamais vos livres, dossiers denses ou vaisselle dans de grands cartons standards. La boîte deviendrait intransportable et risquerait de céder. Réservez les grands volumes aux objets légers (couettes, peluches) et utilisez nos cartons compacts renforcés pour le lourd.",
            },
            {
              n: "Erreur n°3",
              title: "Croiser les rabats pour fermer",
              body: "Emboîter les rabats d'un carton en les croisant sans mettre de scotch fragilise considérablement sa structure. Rabattez toujours les petits côtés en premier, puis les grands, et scellez l'ouverture avec une large bande d'adhésif professionnel pour bloquer l'ensemble.",
            },
          ].map(({ n, title, body }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  {n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Nos autres secteurs (78) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos autres secteurs de livraison dans les {city.deptName}
            {" "}({city.deptCode})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous déménagiez au sein {city.deptNameWithArticle} ou vers une
            commune voisine, nous assurons la livraison de vos cartons de
            déménagement et de vos fournitures d&apos;emballage dans tout le
            département. Grâce à notre organisation locale, nous pouvons
            répondre rapidement aux besoins des particuliers et des
            professionnels.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque jour, nos équipes accompagnent de nombreux clients dans
            leurs projets de déménagement. Découvrez ci-dessous les autres
            secteurs de livraison à proximité de {city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-versailles-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-versailles-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-versailles-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
